import IconButton from 'components/IconButton';
import useIsDesktop from 'hooks/useIsDesktop';
import React, { useContext, useMemo } from 'react';
import {
  Badge,
  Card, CardContent, CardMedia, Stack, Typography,
} from '@mui/material';
import Price from 'components/Price';
import { Link } from 'react-router-dom';
import { getItemImage, getItemPrices } from 'utils/item';
import Skeleton from 'components/Skeleton';
import { CartContext } from 'constants/contexts';
import DietaryTags from 'components/DietaryTags';
import useItemPath from 'hooks/useItemPath';
import { env } from 'constants';

function ItemCard({ item }) {
  const image = getItemImage(item);
  const { getQty } = useContext(CartContext);
  const isDesktop = useIsDesktop();
  const qty = useMemo(() => (item?.id ? getQty(item.id) : 0), [getQty, item.id]);
  const servingSize = item.minimum_size?.serving_size || 1;
  const path = useItemPath(item);
  return (
    <Card
      sx={{
        display: 'flex',
        height: {
          xs: 202,
          md: 176,
        },
        textDecoration: 'none',
        width: '100%',
        py: {
          xs: 1.5,
          md: 0,
        },
        position: 'relative',
        borderRadius: {
          xs: 0,
          md: 4,
        },
        borderWidth: {
          xs: 0,
          md: 1,
        },
        '&:hover': isDesktop ? {
          borderColor: 'black',
          WebkitTransition: 'border 2.5s ease',
          MozTransition: 'border 2.5s ease',
          OTransition: 'border 2.5s ease',
          msTransition: 'border 2.5s ease',
          transition: 'border 2.5s ease, transform 0.3s ease',
          transform: 'scale(1.03)',
        } : {},
      }}
      component={path ? Link : 'div'}
      rel="nofollow"
      to={{ pathname: path, state: { item } }}
      variant="outlined"
    >
      {
        qty > 0 ? (
          <Badge
            badgeContent={qty}
            color="primary"
            max={1000}
            sx={{
              position: 'absolute',
              right: 24,
              bottom: {
                xs: 40,
                md: 28,
              },
              '& span': {
                borderRadius: '12px',
                fontSize: 14,
                fontWeight: 700,
                height: 40,
                width: 40,
              },
            }}
          />
        ) : (
          <IconButton
            name="plus"
            sx={{
              position: 'absolute',
              backgroundColor: 'background.elevation1',
              borderRadius: {
                xs: 3,
                md: 2,
              },
              width: 40,
              height: 40,
              right: {
                xs: 4,
                md: 8,
              },
              bottom: {
                xs: 16,
                md: 8,
              },
            }}
          />
        )
      }

      <Stack position="relative" width="100%" sx={{ flexShrink: 1, minWidth: 0 }}>
        <CardContent
          sx={{
            flex: '1 0 auto',
            p: {
              xs: '4px 8px 0px 0',
              md: '12px 8px 12px 16px',
            },
          }}
        >
          <Stack justifyContent="space-between" height="100%">
            <Stack>
              <Typography
                variant="title-md"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.name}
              </Typography>
              <Typography variant="body-overline" fontWeight="bold">
                {(env.IS_CATERING && !!item.catering_label) && `Serves ${servingSize > 1 ? `${servingSize} people` : '1 person'}`}
              </Typography>

              <Typography
                variant="body-caption"
                color="text.secondary"
                mt={0.5}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.description}
              </Typography>
              <DietaryTags
                tags={item.dietary_tags}
                sx={{
                  '&:after': {
                    content: '""',
                    height: 30,
                    width: 56,
                    right: 0,
                    background: 'linear-gradient(270deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                    position: 'absolute',
                  },
                }}
              />
            </Stack>
            <Price {...getItemPrices({ item })} horizontal showPercent sx={{ mt: 0.5 }} />
          </Stack>
        </CardContent>
      </Stack>
      {image && (
        <CardMedia
          component="img"
          sx={{
            width: {
              xs: 134,
              md: 150,
            },
            flexShrink: 0,
            borderRadius: {
              xs: 4,
              md: '0 16px 16px 0',
            },
          }}
          image={image}
          alt={item.name}
        />
      )}
    </Card>
  );
}

function ItemCardSkeleton() {
  return (
    <Skeleton>
      <ItemCard item={Skeleton.props({
        name: 'string',
        description: ['string', { length: 30 }],
        item_types: [{ size: 1, price: 'price' }],
        dietary_tags: [],
      })}
      />
    </Skeleton>
  );
}

export default Skeleton.withSkeleton(ItemCard, ItemCardSkeleton);
